import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  voiceNumberApi, agentApi,
  type VoicePhoneNumber, type CreateVoicePhoneNumberDto,
} from '@/lib/api';
import { Phone, Plus, Trash2, Edit2, Check, X, ToggleLeft, ToggleRight, PhoneCall } from 'lucide-react';
import styles from './_auth.settings.voice.module.css';
import { PageGuide } from '@/components/PageGuide';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SettingsEmptyState } from '@/components/SettingsEmptyState';

export const Route = createFileRoute('/_auth/settings/voice')({
  component: VoiceNumbersPage,
});

// ── Supported TTS voices ──────────────────────────────────────────────────

const TTS_VOICES = [
  { value: 'Polly.Joanna-Neural', label: 'Joanna (English US)' },
  { value: 'Polly.Matthew-Neural', label: 'Matthew (English US)' },
  { value: 'Polly.Amy-Neural', label: 'Amy (English UK)' },
  { value: 'Polly.Suvi-Neural', label: 'Suvi (Finnish)' },
  { value: 'Polly.Astrid-Neural', label: 'Astrid (Swedish)' },
  { value: 'Polly.Vicki-Neural', label: 'Vicki (German)' },
  { value: 'Polly.Lucia-Neural', label: 'Lucia (Spanish)' },
  { value: 'Polly.Maja-Neural', label: 'Maja (Polish)' },
  { value: 'Polly.Liv-Neural', label: 'Liv (Norwegian)' },
  { value: 'Polly.Celine-Neural', label: 'Céline (French)' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'fi', label: 'Finnish' },
  { value: 'sv', label: 'Swedish' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
  { value: 'pl', label: 'Polish' },
  { value: 'nb', label: 'Norwegian' },
  { value: 'fr', label: 'French' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'nl', label: 'Dutch' },
];

const EMPTY_FORM: CreateVoicePhoneNumberDto = {
  phoneNumber: '',
  displayName: '',
  agentId: undefined,
  language: 'en',
  ttsVoice: 'Polly.Joanna-Neural',
  welcomeMessage: 'Hello! How can I help you today?',
  noInputMessage: "I didn't hear anything. Please say your question.",
  handoffPhoneNumber: '',
  handoffMessage: 'Please hold while I connect you with a team member.',
  maxAiTurns: 8,
  active: true,
};

// ── Main page ─────────────────────────────────────────────────────────────

export default function VoiceNumbersPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<VoicePhoneNumber | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateVoicePhoneNumberDto>(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: numbers = [], isLoading } = useQuery({
    queryKey: ['voiceNumbers'],
    queryFn: voiceNumberApi.list,
  });

  const { data: agents = [] } = useQuery({
    queryKey: ['agents'],
    queryFn: () => agentApi.list(),
  });

  const createMut = useMutation({
    mutationFn: (dto: CreateVoicePhoneNumberDto) => voiceNumberApi.create(dto),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['voiceNumbers'] }); setCreating(false); setForm(EMPTY_FORM); },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Partial<CreateVoicePhoneNumberDto> }) =>
      voiceNumberApi.update(id, dto),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['voiceNumbers'] }); setEditing(null); },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => voiceNumberApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['voiceNumbers'] }); setDeleteConfirm(null); },
  });

  const toggleActive = (num: VoicePhoneNumber) =>
    updateMut.mutate({ id: num.id, dto: { active: !num.active } });

  const openEdit = (num: VoicePhoneNumber) => {
    setEditing(num);
    setForm({
      phoneNumber: num.phoneNumber,
      displayName: num.displayName,
      agentId: num.agentId ?? undefined,
      language: num.language,
      ttsVoice: num.ttsVoice,
      welcomeMessage: num.welcomeMessage,
      noInputMessage: num.noInputMessage,
      handoffPhoneNumber: num.handoffPhoneNumber ?? '',
      handoffMessage: num.handoffMessage,
      maxAiTurns: num.maxAiTurns,
      active: num.active,
    });
  };

  const handleSave = () => {
    const dto = {
      ...form,
      handoffPhoneNumber: form.handoffPhoneNumber?.trim() || undefined,
      agentId: form.agentId?.trim() || undefined,
    };
    if (editing) updateMut.mutate({ id: editing.id, dto });
    else createMut.mutate(dto as CreateVoicePhoneNumberDto);
  };

  const showForm = creating || !!editing;

  return (
    <div className={styles.page}>
      <PageGuide pageKey="voice" />

      {/* ── Header ── */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}><Phone size={22} /> Voice Numbers</h1>
          <p className={styles.subtitle}>
            Configure Twilio phone numbers for the AI voice channel.
            Each number is independently mapped to an agent with its own language, voice, and handoff settings.
          </p>
        </div>
        {!showForm && (
          <button className={styles.primaryBtn} onClick={() => { setCreating(true); setForm(EMPTY_FORM); }}>
            <Plus size={16} /> Add Number
          </button>
        )}
      </div>

      {/* ── Create / Edit Form ── */}
      {showForm && (
        <div className={styles.formCard}>
          <h2 className={styles.formTitle}>
            {editing ? `Edit: ${editing.displayName}` : 'New Phone Number'}
          </h2>

          <div className={styles.formGrid}>
            {/* Phone number */}
            <label className={styles.fieldGroup}>
              <span className={styles.label}>Phone Number (E.164) <span className={styles.required}>*</span></span>
              <input
                className={styles.input}
                placeholder="+15551234567"
                value={form.phoneNumber}
                onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value }))}
              />
              <span className={styles.hint}>Must match exactly what Twilio sends as the To field.</span>
            </label>

            {/* Display name */}
            <label className={styles.fieldGroup}>
              <span className={styles.label}>Display Name <span className={styles.required}>*</span></span>
              <input
                className={styles.input}
                placeholder="Customer Support"
                value={form.displayName}
                onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
              />
            </label>

            {/* Agent */}
            <label className={styles.fieldGroup}>
              <span className={styles.label}>AI Agent</span>
              <select
                className={styles.input}
                value={form.agentId ?? ''}
                onChange={e => setForm(f => ({ ...f, agentId: e.target.value || undefined }))}
              >
                <option value="">— Auto-select —</option>
                {agents.map((a: { id: string; name: string }) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </label>

            {/* Language */}
            <label className={styles.fieldGroup}>
              <span className={styles.label}>Language</span>
              <select
                className={styles.input}
                value={form.language}
                onChange={e => setForm(f => ({ ...f, language: e.target.value }))}
              >
                {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </label>

            {/* TTS Voice */}
            <label className={styles.fieldGroup}>
              <span className={styles.label}>TTS Voice</span>
              <select
                className={styles.input}
                value={form.ttsVoice}
                onChange={e => setForm(f => ({ ...f, ttsVoice: e.target.value }))}
              >
                {TTS_VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
              <span className={styles.hint}>Always use Neural voices for best quality.</span>
            </label>

            {/* Max AI turns */}
            <label className={styles.fieldGroup}>
              <span className={styles.label}>Max AI Turns</span>
              <input
                type="number" min={1} max={50}
                className={styles.input}
                value={form.maxAiTurns}
                onChange={e => setForm(f => ({ ...f, maxAiTurns: parseInt(e.target.value) || 8 }))}
              />
              <span className={styles.hint}>After this many AI replies, the call transfers to the handoff number.</span>
            </label>

            {/* Welcome message — full width */}
            <label className={`${styles.fieldGroup} ${styles.fullWidth}`}>
              <span className={styles.label}>Welcome Message <span className={styles.required}>*</span></span>
              <textarea
                className={styles.textarea}
                rows={2}
                placeholder="Hello! How can I help you today?"
                value={form.welcomeMessage}
                onChange={e => setForm(f => ({ ...f, welcomeMessage: e.target.value }))}
              />
            </label>

            {/* No input message */}
            <label className={`${styles.fieldGroup} ${styles.fullWidth}`}>
              <span className={styles.label}>No Input Message</span>
              <textarea
                className={styles.textarea}
                rows={2}
                value={form.noInputMessage}
                onChange={e => setForm(f => ({ ...f, noInputMessage: e.target.value }))}
              />
              <span className={styles.hint}>Spoken when the caller is silent. Also the fallback if gather times out.</span>
            </label>

            {/* Handoff number */}
            <label className={styles.fieldGroup}>
              <span className={styles.label}>Handoff Phone Number</span>
              <input
                className={styles.input}
                placeholder="+15559876543 (optional)"
                value={form.handoffPhoneNumber ?? ''}
                onChange={e => setForm(f => ({ ...f, handoffPhoneNumber: e.target.value }))}
              />
              <span className={styles.hint}>Leave empty to apologise and hang up instead of transferring.</span>
            </label>

            {/* Handoff message */}
            <label className={styles.fieldGroup}>
              <span className={styles.label}>Handoff Message</span>
              <input
                className={styles.input}
                value={form.handoffMessage}
                onChange={e => setForm(f => ({ ...f, handoffMessage: e.target.value }))}
              />
            </label>
          </div>

          {/* Form actions */}
          <div className={styles.formActions}>
            <button
              className={styles.primaryBtn}
              onClick={handleSave}
              disabled={createMut.isPending || updateMut.isPending || !form.phoneNumber || !form.displayName || !form.welcomeMessage}
            >
              <Check size={15} /> {editing ? 'Save Changes' : 'Create'}
            </button>
            <button className={styles.ghostBtn} onClick={() => { setCreating(false); setEditing(null); }}>
              <X size={15} /> Cancel
            </button>
          </div>
          {(createMut.isError || updateMut.isError) && (
            <p className={styles.errorMsg}>
              {(createMut.error as Error)?.message || (updateMut.error as Error)?.message}
            </p>
          )}
        </div>
      )}

      {/* ── Number list ── */}
      {isLoading ? (
        <div className={styles.empty}>Loading…</div>
      ) : numbers.length === 0 && !showForm ? (
        <SettingsEmptyState
          icons={[
            { icon: <Phone size={18} />, color: '#6366f1' },
            { icon: <PhoneCall size={18} />, color: '#10b981' },
          ]}
          title="No voice numbers configured"
          description="Add a phone number to start routing inbound calls through the AI agent. Each number maps to an agent with its own language and voice."
          buttonLabel="Add Number"
          onAction={() => { setCreating(true); setForm(EMPTY_FORM); }}
        />
      ) : (
        <div className={styles.list}>
          {numbers.map((num: VoicePhoneNumber) => (
            <div key={num.id} className={`${styles.card} ${!num.active ? styles.inactive : ''}`}>
              <div className={styles.cardLeft}>
                <div className={styles.cardIcon}>
                  <Phone size={18} />
                </div>
                <div>
                  <div className={styles.cardTitle}>{num.displayName}</div>
                  <div className={styles.cardNumber}>{num.phoneNumber}</div>
                  <div className={styles.cardMeta}>
                    {num.agent?.name ?? 'Auto-select agent'} · {num.language.toUpperCase()} · {num.ttsVoice} · {num.maxAiTurns} turns
                    {num.handoffPhoneNumber && ` · Handoff → ${num.handoffPhoneNumber}`}
                  </div>
                </div>
              </div>
              <div className={styles.cardActions}>
                {/* Active toggle */}
                <button
                  className={styles.iconBtn}
                  onClick={() => toggleActive(num)}
                  title={num.active ? 'Deactivate' : 'Activate'}
                >
                  {num.active
                    ? <ToggleRight size={22} className={styles.activeIcon} />
                    : <ToggleLeft size={22} className={styles.inactiveIcon} />}
                </button>

                {/* Edit */}
                <button className={styles.iconBtn} onClick={() => openEdit(num)} title="Edit">
                  <Edit2 size={16} />
                </button>

                {/* Delete */}
                <button className={styles.iconBtn} onClick={() => setDeleteConfirm(num.id)} title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Webhook URL guide ── */}
      {numbers.length > 0 && (
        <div className={styles.webhookGuide}>
          <h3 className={styles.guideTitle}>Twilio Webhook Configuration</h3>
          <p className={styles.guideText}>
            Configure these URLs in the <strong>Twilio Console → Phone Numbers → Active Numbers</strong> for each number:
          </p>
          <table className={styles.guideTable}>
            <thead>
              <tr><th>Webhook</th><th>URL</th><th>Method</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Voice (inbound)</td>
                <td><code>{'{API_BASE_URL}'}/v1/channels/voice/<strong>{'{tenantSlug}'}</strong>/inbound</code></td>
                <td>POST</td>
              </tr>
              <tr>
                <td>Status Callback</td>
                <td><code>{'{API_BASE_URL}'}/v1/channels/voice/<strong>{'{tenantSlug}'}</strong>/status</code></td>
                <td>POST</td>
              </tr>
            </tbody>
          </table>
          <p className={styles.guideHint}>
            The <code>{'{tenantSlug}'}</code> is your tenant identifier — it ensures calls are routed to the correct tenant.
            Each Twilio account should point to exactly one tenant slug.
          </p>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => { if (deleteConfirm) deleteMut.mutate(deleteConfirm); }}
        title="Delete voice number?"
        description="This will remove the phone number configuration. Inbound calls to this number will no longer be routed to the AI agent."
        confirmLabel="Delete Number"
        variant="danger"
      />
    </div>
  );
}
